/**
 * Cookie configuration utilities for handling authentication cookies across different environments
 */

import Cookies from 'js-cookie';
import firebase from 'firebase/app';

const COOKIE_NAME = 'villa_auth_token';

/**
 * Get the appropriate cookie domain based on the current environment
 */
export function getCookieDomain(): string | undefined {
  // Only run this on the client side
  if (typeof window === 'undefined') {
    return undefined;
  }

  // Allow environment variable override
  if (process.env.REACT_APP_COOKIE_DOMAIN) {
    return process.env.REACT_APP_COOKIE_DOMAIN;
  }

  const hostname = window.location.hostname;
  
  // Production domains - use root domain for subdomain sharing
  if (hostname === 'villahomes.com' || hostname === 'www.villahomes.com') {
    return '.villahomes.com';
  }
  
  // Staging domain
  if (hostname === 'villa-app-staging.com' || hostname === 'www.villa-app-staging.com') {
    return '.villa-app-staging.com';
  }
  
  // Development domain
  if (hostname === 'villa-app-development.com' || hostname === 'www.villa-app-development.com') {
    return '.villa-app-development.com';
  }
  
  // Vercel preview domains (*.vercel.app)
  if (hostname.endsWith('.vercel.app')) {
    // For Vercel previews, don't set domain (use exact hostname)
    return undefined;
  }
  
  // Local development or unknown domains
  if (hostname === 'localhost' || hostname.startsWith('127.0.0.1') || hostname.includes('local')) {
    return undefined;
  }
  
  // Default: don't set domain for unknown hosts
  return undefined;
}

/**
 * Get cookie configuration with appropriate domain
 */
export function getCookieConfig(): Cookies.CookieAttributes {
  const domain = getCookieDomain();
  const config: Cookies.CookieAttributes = {
    expires: 7, // 7 days
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
    path: '/'
  };

  // Only set domain if we have one
  if (domain) {
    config.domain = domain;
  }
  
  // Log cookie configuration for debugging
  if (typeof window !== 'undefined') {
    console.log(`🍪 Cookie config for ${window.location.hostname}:`, { 
      domain, 
      secure: config.secure,
      sameSite: config.sameSite,
      expires: config.expires
    });
  }
  
  return config;
}

/**
 * Get cookie deletion configuration
 */
export function getCookieDeleteConfig(): Cookies.CookieAttributes {
  const domain = getCookieDomain();
  const config: Cookies.CookieAttributes = {
    path: '/'
  };

  if (domain) {
    config.domain = domain;
  }

  return config;
}

export interface UserInfo {
  uid: string;
  email: string | null;
  displayName: string | null;
  firstName?: string;
  lastName?: string;
  emailVerified: boolean;
}

// Decode user info from Firebase JWT token
const decodeUserInfoFromToken = (token: string): UserInfo | null => {
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Decode the payload (middle part)
    const payload = JSON.parse(atob(parts[1]));
    
    return {
      uid: payload.user_id || payload.sub,
      email: payload.email || null,
      displayName: payload.name || null,
      emailVerified: payload.email_verified || false,
    };
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const setAuthCookie = async (user: firebase.User) => {
  try {
    // Get the Firebase ID token
    const idToken = await user.getIdToken();
    
    // Check if this is a token refresh by comparing with existing token
    const existingToken = Cookies.get(COOKIE_NAME);
    const isTokenRefresh = existingToken && existingToken !== idToken;

    const cookieConfig = getCookieConfig();

    // Set the token cookie
    Cookies.set(COOKIE_NAME, idToken, cookieConfig);

    if (isTokenRefresh) {
      console.log('🔄 Auth token refreshed');
    } else {
      console.log('🍪 Auth cookie set');
    }
  } catch (error) {
    console.error('Error setting auth cookie:', error);
  }
};

export const clearAuthCookies = () => {
  const deleteConfig = getCookieDeleteConfig();

  // Clear cookie with the configured domain
  Cookies.remove(COOKIE_NAME, deleteConfig);

  // Also clear without domain (for exact hostname cookies)
  Cookies.remove(COOKIE_NAME, { path: '/' });

  // For staging/preview environments, also try clearing potential duplicate domains
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Clear potential non-dotted versions for known domains
    if (hostname.includes('villa-app-staging.com')) {
      Cookies.remove(COOKIE_NAME, { path: '/', domain: 'villa-app-staging.com' });
    }
    if (hostname.includes('villa-app-development.com')) {
      Cookies.remove(COOKIE_NAME, { path: '/', domain: 'villa-app-development.com' });
    }
    if (hostname.includes('villahomes.com')) {
      Cookies.remove(COOKIE_NAME, { path: '/', domain: 'villahomes.com' });
    }
  }

  console.log('🗑️ Auth cookies cleared for domain:', deleteConfig.domain || 'current domain');
};

export const getAuthToken = (): string | undefined => {
  return Cookies.get(COOKIE_NAME);
};

export const getUserInfo = (): UserInfo | null => {
  const token = getAuthToken();
  if (!token) return null;
  
  return decodeUserInfoFromToken(token);
};

export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

/**
 * Debug function to check auth cookie configuration and status
 */
export const debugAuthCookies = () => {
  if (typeof window === 'undefined') {
    console.log('🍪 Debug: Running on server side');
    return;
  }
  
  const hostname = window.location.hostname;
  const domain = getCookieDomain();
  const config = getCookieConfig();
  
  const allCookies = document.cookie.split(';');
  const authCookies = allCookies
    .filter(cookie => cookie.trim().startsWith('villa_auth_token='))
    .map(cookie => cookie.trim());
  
  const debugInfo = {
    environment: {
      hostname,
      protocol: window.location.protocol,
      cookieDomain: domain,
      envOverride: process.env.REACT_APP_COOKIE_DOMAIN || null,
      nodeEnv: process.env.NODE_ENV
    },
    cookieConfig: config,
    cookies: {
      found: authCookies.length,
      values: authCookies.map(c => c.substring(0, 50) + '...'), // Truncated for security
      jsLookup: Cookies.get(COOKIE_NAME) ? 'Found via js-cookie' : 'Not found via js-cookie'
    },
    recommendations: getRecommendations(hostname, domain)
  };
  
  console.log('🍪 Auth cookie debug info:', debugInfo);
  return debugInfo;
};

/**
 * Provide environment-specific recommendations
 */
const getRecommendations = (hostname: string, domain: string | undefined) => {
  const recommendations = [];
  
  if (hostname.includes('www.') && !domain) {
    recommendations.push('Consider setting domain for www subdomain access');
  }
  
  if (hostname.endsWith('.vercel.app') && domain) {
    recommendations.push('Vercel preview URLs should not share cookies - consider removing REACT_APP_COOKIE_DOMAIN');
  }
  
  if ((hostname === 'localhost' || hostname.includes('local')) && domain) {
    recommendations.push('Local development usually doesn\'t need cookie domain setting');
  }
  
  if (!domain && hostname.includes('.')) {
    recommendations.push('If you need subdomain cookie sharing, set REACT_APP_COOKIE_DOMAIN');
  }
  
  return recommendations;
};